import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { FileText, Download, Clock, CheckCircle, IdCard, Award, Mail, Lock } from "lucide-react";
import jsPDF from "jspdf";

export default function Documents() {
  const { currentUser, userProfile } = useAuth();
  const [docs,       setDocs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [generating, setGenerating] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const q    = query(collection(db, "documents"), where("userId", "==", currentUser.uid));
        const snap = await getDocs(q);
        setDocs(snap.docs.map(d => ({ id:d.id, ...d.data() })));
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    }
    if (currentUser) load();
  }, [currentUser]);

  // ── Generate ID Card PDF ──
  function generateIdCard() {
    setGenerating("id");
    const doc = new jsPDF({ orientation:"landscape", unit:"mm", format:[85.6,53.98] });
    doc.setFillColor(17,16,9);
    doc.rect(0,0,85.6,53.98,"F");
    doc.setFillColor(245,158,11);
    doc.rect(0,0,85.6,8,"F");
    doc.setTextColor(255,255,255);
    doc.setFontSize(7); doc.setFont("helvetica","bold");
    doc.text("HELP THEM SHINE TRUST · MEMBER PORTAL",42.8,5,{align:"center"});
    doc.setFontSize(14); doc.setFont("helvetica","bold");
    doc.text(userProfile?.name || "Member Name",42.8,22,{align:"center"});
    doc.setFontSize(8); doc.setFont("helvetica","normal");
    doc.setTextColor(168,162,158);
    doc.text(`ID: ${userProfile?.memberId || "HTS-0000"}`,42.8,28,{align:"center"});
    doc.text(`Role: ${(userProfile?.role||"member").toUpperCase()}`,42.8,33,{align:"center"});
    doc.text(`Email: ${currentUser?.email}`,42.8,38,{align:"center"});
    doc.setFillColor(245,158,11);
    doc.rect(0,47,85.6,7,"F");
    doc.setTextColor(10,9,8);
    doc.setFontSize(6); doc.setFont("helvetica","bold");
    doc.text("htstrust@gmail.com · +91 7669990505 · Ghaziabad, UP",42.8,51.5,{align:"center"});
    doc.save(`HTS_ID_Card_${userProfile?.name||"Member"}.pdf`);
    setGenerating("");
  }

  // ── Participation certificates from Firestore (admin-uploaded) ──
  const certificates = docs.filter(d => d.type === "certificate");
  const otherDocs    = docs.filter(d => d.type !== "certificate");

  return (
    <div style={{ maxWidth:700 }}>

      {/* ── ID Card ── */}
      <div style={s.card}>
        <div style={s.cardTitle}><IdCard size={16} color="#f59e0b"/> Auto-Generated Documents</div>
        <div style={s.row}>
          <div style={{ ...s.docIcon, background:"rgba(59,130,246,0.12)" }}>
            <IdCard size={20} color="#3b82f6"/>
          </div>
          <div style={{ flex:1 }}>
            <div style={s.rowTitle}>Digital ID Card</div>
            <div style={s.rowSub}>Your official HTS member ID — always available</div>
          </div>
          <span style={{ ...s.badge, background:"rgba(34,197,94,0.12)", color:"#22c55e" }}>
            <CheckCircle size={11}/> Always Available
          </span>
          <button onClick={generateIdCard} disabled={generating==="id"}
            style={{ ...s.dlBtn, opacity:generating==="id"?0.6:1 }}>
            <Download size={14}/> {generating==="id" ? "..." : "Download PDF"}
          </button>
        </div>
      </div>

      {/* ── Participation Certificates ── */}
      <div style={s.card}>
        <div style={{ ...s.cardTitle, marginBottom:6 }}>
          <Award size={16} color="#f59e0b"/> Participation Certificates
        </div>
        <div style={{ fontSize:12, color:"#52504d", marginBottom:16, display:"flex", alignItems:"center", gap:6 }}>
          <Lock size={11} color="#52504d"/>
          Certificates are issued by admin. You'll be notified when one is available.
        </div>

        {loading ? (
          <div style={s.empty}>Loading certificates...</div>
        ) : certificates.length === 0 ? (
          <div style={{ textAlign:"center", padding:"32px 0" }}>
            <Award size={40} color="#1a1714" style={{ marginBottom:12 }}/>
            <div style={{ fontSize:14, fontWeight:600, color:"#52504d", marginBottom:6 }}>
              No certificates issued yet
            </div>
            <div style={{ fontSize:12, color:"#3a3835" }}>
              Your admin will upload certificates after events & drives
            </div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column" }}>
            {certificates.map(cert => (
              <div key={cert.id} style={s.row}>
                <div style={{ ...s.docIcon, background:"rgba(245,158,11,0.12)" }}>
                  <Award size={20} color="#f59e0b"/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={s.rowTitle}>{cert.title || "Participation Certificate"}</div>
                  <div style={s.rowSub}>
                    {cert.eventName && <span>{cert.eventName} · </span>}
                    Issued: {cert.issuedDate || "—"}
                  </div>
                </div>
                {cert.url ? (
                  <a href={cert.url} target="_blank" rel="noreferrer" style={s.dlBtn}>
                    <Download size={14}/> Download
                  </a>
                ) : (
                  <span style={{ ...s.badge, background:"rgba(245,158,11,0.12)", color:"#f59e0b" }}>
                    <Clock size={11}/> Processing
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Other Admin Documents ── */}
      {otherDocs.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}><FileText size={16} color="#f59e0b"/> Other Documents</div>
          {otherDocs.map(d => (
            <div key={d.id} style={s.row}>
              <div style={{ ...s.docIcon, background:"rgba(59,130,246,0.12)" }}>
                <FileText size={18} color="#3b82f6"/>
              </div>
              <div style={{ flex:1 }}>
                <div style={s.rowTitle}>{d.title}</div>
                <div style={s.rowSub}>Issued: {d.issuedDate || "—"}</div>
              </div>
              {d.url ? (
                <a href={d.url} target="_blank" rel="noreferrer" style={s.dlBtn}>
                  <Download size={14}/> Download
                </a>
              ) : (
                <span style={{ ...s.badge, background:"rgba(245,158,11,0.12)", color:"#f59e0b" }}>
                  <Clock size={11}/> Pending
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Contact ── */}
      <div style={s.noteBox}>
        <Mail size={18} color="#3b82f6"/>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:"#fef3e2", marginBottom:4 }}>
            Need a document?
          </div>
          <div style={{ fontSize:12, color:"#78716c", lineHeight:1.6 }}>
            Contact admin at <span style={{ color:"#f59e0b" }}>htstrust@gmail.com</span> or
            call <span style={{ color:"#f59e0b" }}>+91 7669990505</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  card:    { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20, marginBottom:16 },
  cardTitle:{ fontSize:14, fontWeight:700, color:"#fef3e2", marginBottom:16, display:"flex", alignItems:"center", gap:8 },
  row:     { display:"flex", alignItems:"center", gap:12, padding:"12px 0", borderBottom:"1px solid rgba(255,255,255,0.04)", flexWrap:"wrap" },
  docIcon: { width:44, height:44, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  rowTitle:{ fontSize:13, fontWeight:600, color:"#fef3e2" },
  rowSub:  { fontSize:11, color:"#52504d", marginTop:2 },
  badge:   { display:"inline-flex", alignItems:"center", gap:4, fontSize:10, fontWeight:700, padding:"4px 10px", borderRadius:50 },
  dlBtn:   { display:"inline-flex", alignItems:"center", gap:6, background:"rgba(245,158,11,0.12)", color:"#f59e0b", border:"1px solid rgba(245,158,11,0.25)", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700, textDecoration:"none", cursor:"pointer", fontFamily:"inherit" },
  noteBox: { background:"rgba(59,130,246,0.06)", border:"1px solid rgba(59,130,246,0.15)", borderRadius:12, padding:16, display:"flex", gap:12, alignItems:"flex-start" },
  empty:   { fontSize:13, color:"#52504d", textAlign:"center", padding:"24px 0" },
};